/**
 * Every unit's scope of service, in one list.
 *
 * Loaded in full: a hospital has tens of units, not thousands, so the whole
 * set comes down in one call and every filter runs in the browser. The
 * filters live in the URL so that opening a unit and coming back does not
 * lose them — the lesson the policy list learned the hard way.
 *
 * GATED BY THE ADD-ON. When the organization has not turned this on, the
 * page says so rather than calling a function that will refuse it.
 */

import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { hasAtLeast } from '../../types/roles';
import { FILTER_MAX } from '../../types/fieldLimits';
import { useAddons } from '../org/useAddons';
import { useTaxonomy } from '../admin/useTaxonomy';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { SegmentedChoice } from '../../shared/SegmentedChoice';
import { isCallFailure } from '../../lib/callable';
import { useScopes } from './useScopes';
import { countByStatus, filterScopes, NO_FILTER, type ScopeFilter } from './scopeFilters';
import { formatDay, SCOPE_STATUSES, STATUS_LABEL, STATUS_TONE, type ScopeStatus } from './types';
import { ImportScopesPanel } from './ImportScopesPanel';
import styles from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import own from './ScopeOfService.module.css';

export const LOCKED_TITLE = 'Scope of service is not turned on';

function readFilter(params: URLSearchParams): ScopeFilter {
  const status = params.get('status');
  return {
    query: params.get('q') ?? '',
    status: (SCOPE_STATUSES as readonly string[]).includes(status ?? '') ? (status as ScopeStatus) : 'all',
    departmentId: params.get('department') || 'all',
  };
}

function writeFilter(f: ScopeFilter): URLSearchParams {
  const p = new URLSearchParams();
  if (f.query.trim()) p.set('q', f.query);
  if (f.status !== 'all') p.set('status', f.status);
  if (f.departmentId !== 'all') p.set('department', f.departmentId);
  return p;
}

export function ScopeListPage() {
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const roles = session?.roles ?? [];
  const author = hasAtLeast(roles, 'author');

  const addons = useAddons(orgId);
  const enabled = addons.data?.scopeOfService === true;

  const scopes = useScopes(orgId, enabled);
  const departments = useTaxonomy('department', enabled);
  const departmentName = useMemo(() => {
    const byId = new Map((departments.data ?? []).map((d) => [d.id, d.name]));
    return (id: string | null) => (id ? (byId.get(id) ?? '') : '');
  }, [departments.data]);

  const [params, setParams] = useSearchParams();
  const filter = useMemo(() => readFilter(params), [params]);
  const setFilter = (next: Partial<ScopeFilter>) =>
    setParams(writeFilter({ ...filter, ...next }), { replace: true });
  const [importing, setImporting] = useState(false);

  const rows = scopes.data ?? [];
  const shown = useMemo(() => filterScopes(rows, filter, departmentName), [rows, filter, departmentName]);
  const counts = useMemo(() => countByStatus(rows), [rows]);
  const filtered = filter.query.trim() !== '' || filter.status !== 'all' || filter.departmentId !== 'all';

  // Only departments that at least one unit belongs to: a dropdown of every
  // department the organization has ever configured mostly matches nothing.
  const departmentOptions = useMemo(() => {
    const present = new Set(rows.map((r) => r.departmentId).filter(Boolean));
    return (departments.data ?? []).filter((d) => present.has(d.id));
  }, [rows, departments.data]);

  if (addons.isLoading) return <Spinner label="Loading" />;

  if (!enabled) {
    return (
      <>
        <header className={styles.header}>
          <h1 className={styles.title}>Scope of service</h1>
        </header>
        <div className={styles.body}>
          <PageState title={LOCKED_TITLE}>
            This add-on is not enabled for your organization. Your StandingDocs administrator can turn
            it on; nothing here is lost in the meantime.
          </PageState>
        </div>
      </>
    );
  }

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerRow}>
          <div>
            <h1 className={styles.title}>Scope of service</h1>
            <p className={styles.subtitle}>
              What each unit is authorized to do: the patients it serves, the services it provides,
              its hours, staffing and equipment. One approved, current document per unit is what a
              surveyor asks to see.
            </p>
          </div>
          {author && (
            <div className={styles.actions}>
              <Link to="/scope-of-service/new" className={`${styles.btn} ${styles.btnPrimary}`}>
                New unit scope
              </Link>
              <button
                type="button"
                className={styles.btn}
                onClick={() => setImporting((v) => !v)}
                aria-expanded={importing}
              >
                Import from CSV
              </button>
            </div>
          )}
        </div>
        {importing && <ImportScopesPanel orgId={orgId} onClose={() => setImporting(false)} />}
      </header>

      <div className={styles.body}>
        {scopes.error && (
          <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
            Could not load the units.{' '}
            {isCallFailure(scopes.error) ? scopes.error.message : ''}
          </div>
        )}

        {scopes.isLoading ? (
          <Spinner label="Loading units" />
        ) : rows.length === 0 ? (
          <div className={`${styles.notice} ${styles.noticeWarn}`} role="status">
            No unit scopes yet.{' '}
            {author
              ? 'Add the first with New unit scope, or bring in a spreadsheet with Import from CSV.'
              : 'An author or administrator adds them.'}
          </div>
        ) : (
          <>
            <div className={own.filters}>
              <div className={own.filterText}>
                <label className={form.label} htmlFor="scope-filter">Filter</label>
                <input
                  id="scope-filter"
                  className={form.input}
                  type="text"
                  maxLength={FILTER_MAX}
                  autoComplete="off"
                  value={filter.query}
                  onChange={(e) => setFilter({ query: e.target.value })}
                  placeholder="Unit name, code or department"
                />
              </div>
              <SegmentedChoice
                legend="Status"
                name="scope-status"
                value={filter.status}
                onChange={(v) => setFilter({ status: v })}
                options={[
                  { value: 'all' as const, label: `All (${rows.length})` },
                  ...SCOPE_STATUSES.map((s) => ({ value: s, label: `${STATUS_LABEL[s]} (${counts[s]})` })),
                ]}
              />
              {departmentOptions.length > 0 && (
                <div className={own.filterSelect}>
                  <label className={form.label} htmlFor="scope-department">Department</label>
                  <select
                    id="scope-department"
                    className={form.select}
                    value={filter.departmentId}
                    onChange={(e) => setFilter({ departmentId: e.target.value })}
                  >
                    <option value="all">Any</option>
                    {departmentOptions.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <p className={own.count} role="status" aria-live="polite">
              {filtered ? (
                <><span><b className="tnum">{shown.length}</b> of <span className="tnum">{rows.length}</span></span>
                  <button type="button" className={own.linkBtn} onClick={() => setParams(writeFilter(NO_FILTER), { replace: true })}>
                    Clear filters
                  </button></>
              ) : (
                <span><b className="tnum">{rows.length}</b> {rows.length === 1 ? 'unit' : 'units'}</span>
              )}
            </p>

            <div className={own.tableWrap}>
              <table className={own.table}>
                <thead>
                  <tr>
                    <th scope="col">Unit</th>
                    <th scope="col">Code</th>
                    <th scope="col">Department</th>
                    <th scope="col">Status</th>
                    <th scope="col">Version</th>
                    <th scope="col">Next review</th>
                    <th scope="col">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((s) => (
                    <tr key={s.id}>
                      <td className={own.unit}>
                        <Link to={`/scope-of-service/${s.id}`}>{s.unitName}</Link>
                      </td>
                      <td className={own.mono}>{s.unitCode}</td>
                      <td className={departmentName(s.departmentId) ? undefined : own.muted}>
                        {departmentName(s.departmentId) || '—'}
                      </td>
                      <td>
                        <span className={`${styles.tag} ${styles[STATUS_TONE[s.status]]}`}>{STATUS_LABEL[s.status]}</span>
                      </td>
                      <td className={own.num}>v{s.version}</td>
                      <td className={`${own.num} ${s.nextReviewDate ? '' : own.muted}`}>{formatDay(s.nextReviewDate)}</td>
                      <td className={`${own.num} ${s.updatedAt ? '' : own.muted}`}>{formatDay(s.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {shown.length === 0 && (
                <p className={styles.subtitle}>
                  Nothing matches. <strong>Clear filters</strong> to see all {rows.length}.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
